import React, { useState, useEffect } from 'react';
import { simStudioApi } from '../api/simStudioApi';
import type { SimulationActor } from '../api/simStudioApi';
import type { Theme } from './StudioHeader';
import { IconSchool, IconTeacher, IconFamily, IconUsers, IconArrowRight } from './Icons';

interface Props {
  escolaId: string | null;
  theme: Theme;
}

// Perfil → metadados visuais
const PERFIL_META: Record<string, {
  Icon: React.FC<any>;
  accentDark: string;
  accentLight: string;
  label: string;
}> = {
  ESCOLA:      { Icon: IconSchool,   accentDark: '#60a5fa', accentLight: '#2563eb', label: 'Escola'       },
  PROFESSOR:   { Icon: IconTeacher,  accentDark: '#4ade80', accentLight: '#16a34a', label: 'Professores'  },
  RESPONSAVEL: { Icon: IconFamily,   accentDark: '#fbbf24', accentLight: '#d97706', label: 'Responsáveis' },
  ALUNO:       { Icon: IconUsers,    accentDark: '#a78bfa', accentLight: '#7c3aed', label: 'Alunos'       },
  EQUIPE:      { Icon: IconUsers,    accentDark: '#34d399', accentLight: '#059669', label: 'Equipe'       },
  FIXO:        { Icon: IconSchool,   accentDark: '#f472b6', accentLight: '#db2777', label: 'Monitoramento' },
};
const FALLBACK_META = { Icon: IconUsers, accentDark: '#94a3b8', accentLight: '#64748b', label: 'Outros' };

function hexToRgb(hex: string) {
  return `${parseInt(hex.slice(1,3),16)},${parseInt(hex.slice(3,5),16)},${parseInt(hex.slice(5,7),16)}`;
}

// Ator com flag de "ator fixo de infra"
interface ActorEx extends SimulationActor {
  _isFixed?: boolean;
}

const DEMO_PASSWORD = 'Admin@2026';

export function ActorQuickLaunch({ escolaId, theme }: Props) {
  const isDark = theme === 'dark';

  const [schoolActors, setSchoolActors] = useState<SimulationActor[]>([]);
  const [fixedActors,  setFixedActors]  = useState<SimulationActor[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [launching,    setLaunching]    = useState<string | null>(null);
  const [copied,       setCopied]       = useState<string | null>(null);

  const labelColor = isDark ? '#475569' : '#94a3b8';
  const cardBg     = isDark ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const nameColor  = isDark ? '#e2e8f0' : '#1e293b';
  const emailColor = isDark ? '#475569' : '#94a3b8';
  const skeleton   = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';

  // Carrega atores da escola
  useEffect(() => {
    if (!escolaId) { setSchoolActors([]); return; }
    setLoading(true);
    simStudioApi.getSchoolActors(escolaId)
      .then(data => setSchoolActors(Array.isArray(data) ? data : []))
      .catch(() => setSchoolActors([]))
      .finally(() => setLoading(false));
  }, [escolaId]);

  // Carrega atores fixos de infra (global, carrega uma vez)
  useEffect(() => {
    simStudioApi.getAtoresFixos()
      .then(data => setFixedActors(Array.isArray(data) ? data : []))
      .catch(() => setFixedActors([]));
  }, []);

  // Magic-login — apenas para @sim.demo.ac.br
  const handleMagicLogin = (actor: ActorEx) => {
    setLaunching(actor.id);
    const origin = window.location.origin;
    window.open(
      `/api/v1/simulation/magic-login-redirect?actorId=${actor.id}&origin=${encodeURIComponent(origin)}`,
      '_blank', 'noopener,noreferrer',
    );
    setTimeout(() => setLaunching(null), 1500);
  };

  // Copiar credenciais — para atores demo normais
  const handleCopyCredentials = (actor: ActorEx) => {
    const text = `Email: ${actor.email}\nSenha: ${DEMO_PASSWORD}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(actor.id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  // Agrupa por perfil
  function groupByPerfil(actors: ActorEx[]): Record<string, ActorEx[]> {
    return actors.reduce<Record<string, ActorEx[]>>((acc, a) => {
      const k = a.perfil ?? 'OUTROS';
      (acc[k] = acc[k] || []).push(a);
      return acc;
    }, {});
  }

  const schoolGroups = groupByPerfil(schoolActors);
  const fixedGroups  = groupByPerfil(fixedActors.map(a => ({ ...a, _isFixed: true })));

  function renderActor(actor: ActorEx, color: string, rgb: string, Icon: React.FC<any>) {
    const isFixed   = actor._isFixed || actor.email?.endsWith('@sim.demo.ac.br');
    const isLaunch  = launching === actor.id;
    const isCopied  = copied === actor.id;

    return (
      <div
        key={actor.id}
        className="flex items-center gap-2.5"
        style={{
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: 10,
          padding: '9px 12px',
        }}
      >
        {/* Ícone */}
        <div style={{
          width: 30, height: 30, borderRadius: 7, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `rgba(${rgb},${isDark ? '0.12' : '0.08'})`,
        }}>
          <Icon size={14} color={color} />
        </div>

        {/* Nome + email */}
        <div className="flex-1 min-w-0">
          <div style={{ fontSize: 13, fontWeight: 600, color: nameColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {actor.nome}
          </div>
          <div style={{ fontSize: 10, color: emailColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {actor.email}
          </div>
        </div>

        {/* Ação */}
        {isFixed ? (
          // Ator fixo → magic login (abre PWA autenticado)
          <button
            onClick={() => handleMagicLogin(actor)}
            disabled={isLaunch}
            title="Abrir no PWA como este ator"
            className="flex-shrink-0 flex items-center justify-center transition-all cursor-pointer disabled:opacity-50"
            style={{
              width: 28, height: 28, borderRadius: 7,
              background: `rgba(${rgb},0.12)`,
              border: `1px solid rgba(${rgb},0.25)`,
            }}
          >
            {isLaunch
              ? <div className="w-3 h-3 border-2 rounded-full animate-spin" style={{ borderColor: color, borderTopColor: 'transparent' }} />
              : <IconArrowRight size={13} color={color} />
            }
          </button>
        ) : (
          // Ator demo → copiar credenciais
          <button
            onClick={() => handleCopyCredentials(actor)}
            title="Copiar email e senha demo"
            className="flex-shrink-0 flex items-center justify-center transition-all cursor-pointer"
            style={{
              width: 28, height: 28, borderRadius: 7, fontSize: 11,
              background: isCopied ? `rgba(${rgb},0.15)` : `rgba(${rgb},0.08)`,
              border: `1px solid rgba(${rgb},${isCopied ? '0.35' : '0.18'})`,
              color,
            }}
          >
            {isCopied ? '✓' : '⎘'}
          </button>
        )}
      </div>
    );
  }

  function renderGroup(perfil: string, actors: ActorEx[], overrideLabel?: string) {
    const meta  = PERFIL_META[perfil] ?? FALLBACK_META;
    const color = isDark ? meta.accentDark : meta.accentLight;
    const rgb   = hexToRgb(color);
    const shown = actors.slice(0, 4);

    return (
      <div key={perfil} className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5 px-0.5">
          <meta.Icon size={12} color={color} />
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color }}>
            {overrideLabel ?? meta.label}
          </span>
        </div>
        {shown.map(a => renderActor(a, color, rgb, meta.Icon))}
        {actors.length > 4 && (
          <p style={{ fontSize: 11, color: labelColor, paddingLeft: 2 }}>+{actors.length - 4} mais</p>
        )}
      </div>
    );
  }

  const noActors = !loading && schoolActors.length === 0 && fixedActors.length === 0;

  return (
    <div className="flex flex-col gap-3">
      {/* Título */}
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: labelColor, paddingLeft: 2 }}>
        Lançar Ator
      </div>

      {!escolaId && (
        <p style={{ fontSize: 12, color: labelColor }}>Selecione uma escola para ver os atores.</p>
      )}

      {/* Skeletons */}
      {loading && Array.from({ length: 3 }).map((_, i) => (
        <div key={i} style={{ height: 48, borderRadius: 10, background: skeleton }} className="animate-pulse" />
      ))}

      {/* Atores da escola agrupados */}
      {!loading && Object.entries(schoolGroups).map(([perfil, list]) =>
        renderGroup(perfil, list)
      )}

      {/* Divisor + Atores fixos de monitoramento */}
      {!loading && fixedActors.length > 0 && (
        <>
          <div style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }} />
          {Object.entries(fixedGroups).map(([perfil, list]) =>
            renderGroup('FIXO', list, 'Monitoramento (fixos)')
          )}
        </>
      )}

      {/* Vazio */}
      {noActors && escolaId && (
        <div style={{
          padding: '10px 12px', borderRadius: 10, fontSize: 12,
          background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`,
          color: labelColor, lineHeight: 1.6,
        }}>
          Nenhum ator encontrado.<br />
          <span style={{ fontSize: 11, opacity: 0.7 }}>Execute o Mega Seed primeiro.</span>
        </div>
      )}

      {/* Legenda */}
      {!loading && (schoolActors.length > 0 || fixedActors.length > 0) && (
        <div style={{
          padding: '7px 10px', borderRadius: 8, fontSize: 10,
          background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          color: labelColor, lineHeight: 1.7,
        }}>
          <span style={{ color: isDark ? '#f472b6' : '#db2777' }}>↗</span> Fixos: abre sessão no PWA<br />
          <span style={{ color: isDark ? '#60a5fa' : '#2563eb' }}>⎘</span> Demo: copia email + senha ({DEMO_PASSWORD})
        </div>
      )}
    </div>
  );
}
